import * as React from 'react';
import moment from 'moment';
import GenericHoc from 'src/components/hoc/GenericHoc';
import { HocOptions } from 'src/components/hoc/GenericHoc.types';
import { vgmStyles } from './document-request.styles';
import { StylesProps } from 'src/Library/Types';
import { Collapse, Icon, Button, message, Spin, Tooltip } from 'antd';
import { RequestStatus } from './document-request.constants';
import { bindActionCreators } from 'redux';
import { isValidArray, stops } from 'src/Library/Helper';
import { getContainerStatus } from '../SETasks/SETaskList/UploadSIModal.utils';
import { deleteForm13Request, fetchForm13RequestStatus } from 'src/api/shipmentExecution';
import { useGenericState } from 'src/Library/UseGenericState';
import { ExecutionDetails } from '../SEDetails/shipment-details.types';
import { fetchAllSEDocuments, fetchForm13RequestDetails } from 'src/actions/shipmentExecutionActions';
import SEDeleteOdexDraftModal from './SEDeleteOdexDraftModal';
import Loader from 'src/components/common/Loader';

interface Form13Props extends StylesProps<typeof vgmStyles> {
	odexMasterData;
  form13Requests: any;
  updatedContainerStatus;
  form13RequestData: any;
  actionsEnabled: boolean;
  pendingForm13Containers;
  odexForm13PyrCode?: string;
  isFetchingShippingData: boolean;
  odexForm13ReferenceNumber?: string;
  setForm13ModalStates: (state) => void;
  shipmentDetails: ExecutionDetails | undefined;
  updateContainerStatus: (containerDetails) => void;
  fetchAllSEDocuments: (shipmentId, isDraft?) => void;
}

interface StateProps {
	isForm13RequestDeleting?: boolean;
	isContainerStatusRefreshing?: boolean;
	isDeleteForm13RequestModalVisible?: boolean;
}

const Form13Docs = (props: Form13Props) => {
  const {
    classes,
    odexMasterData,
    actionsEnabled,
    form13Requests,
    shipmentDetails,
    odexForm13PyrCode,
    form13RequestData,
    fetchAllSEDocuments,
    setForm13ModalStates,
    updateContainerStatus,
    isFetchingShippingData,
    updatedContainerStatus,
    pendingForm13Containers,
    odexForm13ReferenceNumber,
  } = props;

  const [state, setState] = useGenericState<StateProps>({});

	const {
		isContainerStatusRefreshing,
		isForm13RequestDeleting,
		isDeleteForm13RequestModalVisible,
	} = state;

  const [active, setActive] = React.useState(0);

  React.useEffect(() => {
    const form13RequestId = form13Requests?.[active]?.id;
    if (!form13RequestId || (!active && active !== 0)) {
      return;
    }
    fetchForm13RequestDetails(form13RequestId); 
  }, [active]);

  const renderForm13List = () => {
    const isNewRequestDisabled = !actionsEnabled || pendingForm13Containers <= 0;
    return <div className={classes.docList}>
      {form13Requests?.map((item, index) => <div
        key={index}
        onClick={() => setActive(index)}
        className={classes.docCard}
        style={{
          color: active === index ? '#FFFFFF' : 'inherit',
          backgroundColor: active === index ? '#444444' : 'inherit',
          fontWeight: active === index ? 'bold' : 'inherit',
        }}
      >
        <div className={classes.docName}>
          {`Form-13 Request ${index + 1}`}
        </div>
        <div className={classes.docDate}>
          {item.createdAt && moment(item.createdAt).format('DD MMM YYYY | hh:mm A')}
        </div>
      </div>)}
      <div
        className={classes.addDocument}
        onClick={() => {
          if (isNewRequestDisabled) {
            return;
          }
          setForm13ModalStates({ isForm13RequestDetailsModalVisible: true });
        }}
        style={isNewRequestDisabled ?
          { cursor: 'not-allowed', color: 'lightgrey' } :
          undefined
        }
      >
        + New Request
      </div>
    </div>;
  };

  const renderEditAndDelete = () => {
    if (!form13RequestData?.isDraft || !actionsEnabled) {
      return;
    }
      
    return (
      <>
        <Icon type="edit"
          style={{ marginLeft: 8, cursor: pendingForm13Containers <= 0 ? 'not-allowed' : 'pointer' }}
          theme="filled"
          onClick={() => {
            if (pendingForm13Containers <= 0) return;
            setForm13ModalStates({ isForm13UploadModalVisible: true })
          }}
        />
        {isForm13RequestDeleting ? <Spin /> :
          <Icon
            type="delete"
            style={{ marginLeft: 8 }}
            theme="filled"
            onClick={() => setState({ isDeleteForm13RequestModalVisible: true })}
          />}
      </>
    );
  }

  const renderDownload = () => {
    const containerStatus =
      updatedContainerStatus?.[form13RequestData?.id] ||
      form13RequestData?.containerDetails[0]?.status;

    if (
      containerStatus === RequestStatus.VERIFIED ||
      form13RequestData?.containerDetails?.some(container => container.status === RequestStatus.VERIFIED)
    ) {
      return (
        <Tooltip title="This will redirect to Odex to download the necessary files." placement="left">
          <a href={'https://odex.co/loadLanding.do'} target="_blank" rel="noopener noreferrer">
            <Icon type="download" />
          </a>
        </Tooltip>
      );
    }

    return;
  }

  const renderReference = () => {
    return (
      <div className={classes.reference}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className={classes.referenceList}>
            <div className={classes.referenceValue}>{form13RequestData?.referenceNumber || '-'}</div>
            <div className={classes.referenceTitle}>Form-13 Request #</div>
          </div>
          <div className={classes.referenceList}>
          {form13RequestData?.isDraft &&
            <span className={classes.flexRow} style={{ marginLeft: '4px' }}>
              <strong>|</strong> <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 16,
                  backgroundColor: '#666666',
                  margin: '0 4px',
                }}
              /> Draft
            </span>}
          </div>
        </div>
        <div style={{ fontSize: 20 }}>
          {renderDownload()}
          {renderEditAndDelete()}
        </div>
      </div>
    );
  };

  const renderTitle = (title: string) => {
    return <div 
      className={classes.title}>
      {title}
    </div>;
  };
  const renderValue = (value: string | number | undefined, isDate: boolean = false) => {
    if (!value || value === 'N.A.') {
      return <div
        className={classes.na}>
          NA
      </div>;
    }
    if (isDate) {
      return <div
        className={classes.value}>
        {moment(value).format('DD/MM/YYYY | HH:mm A')}
      </div>;
    }
    return <div className={classes.value}>{value}</div>;
  };

  const renderField = (title: string, value: string | number | undefined, isDate: boolean = false) => {
    return <div
      className={classes.flexRow}
      style={{ width: '33%', alignItems: 'normal' }}>
      {renderTitle(title)} {renderValue(value, isDate)}
    </div>;
  };

  const renderContentTitle = (title: string) => {
    return <div 
      className={classes.contentTitle}>
      {title}
    </div>;
  };

  const renderLine = () => {
    return <div className={classes.line}/>;
  };

  const getLabelFromOdexField = (field, key) => {
		const Item = odexMasterData[field]?.find(el => el.key === key);
		return Item?.label;
  }

  const renderShipperBooking = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Shipper / Booking Details')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Booking No.', form13RequestData?.bookingNumber)}
        {renderField('Shipping Line', form13RequestData?.carrier?.name)}
        {renderField('POL', form13RequestData?.pol?.name)}
      </div>
      <div className={classes.contentRow}>
        {renderField('Origin', getLabelFromOdexField('stuffingLocations', form13RequestData?.stuffingLocation))}
        {renderField('Terminal', form13RequestData?.terminalLoginId)}
        {renderField('Vessel', form13RequestData?.vessel)}
      </div>
      <div className={classes.contentRow}>
          {renderField('POD', form13RequestData?.pod)}
          {renderField('FPOD', form13RequestData?.fpod?.name)}
          {renderField('Cargo Type', getLabelFromOdexField('cargoTypes', form13RequestData?.cargoType))}
        </div>
        <div className={classes.contentRow}>
          {renderField('Service', form13RequestData?.service)}
          {renderField('Container Status', getLabelFromOdexField('containerStatus', form13RequestData?.containerStatus))}
          {renderField('VIA No.', form13RequestData?.viaNo)}
        </div>
        <div className={classes.contentRow}>
          {renderField('CFS', 'Mumbai')}
          {renderField('SI No.', form13RequestData?.siNo)}
        </div>
    </div>;
  };

  const renderContractPartyDetails = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Contract Party')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Mobile', form13RequestData?.notifyPartyContact)}
        {renderField('Notify To', isValidArray(form13RequestData?.notifyParties) && form13RequestData?.notifyParties?.join(', '))}
      </div>
    </div>;
  };

  const renderCustomerSection = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Customer Details')}
      {renderLine()}
      <div className={classes.contentRow}>
        {renderField('Issue To', getLabelFromOdexField('issueTo', form13RequestData?.issueTo))}
        {renderField('Shipper Name', form13RequestData?.shipperOrganisationName)}
        {renderField('CHA Name', odexForm13PyrCode)}
      </div>
      <div className={classes.contentRow}>
        {renderField('CHA Num', odexForm13ReferenceNumber)}
      </div>
    </div>;
  };

  const refreshContainerStatus = async (containerNumber) => {
    setState({ isContainerStatusRefreshing: true });
		const response = await fetchForm13RequestStatus({
			containerNumber,
		}, form13RequestData?.id)
		if (!response.isSuccess) {
			message.error(response.errorMessage)
		} else {
			updateContainerStatus(isSingleContainer ?
				{
					[`${form13RequestData?.id}`]: response?.data?.containerStatus
				}
				:
				{
					[`${containerNumber}`]: response?.data?.containerStatus
				}
			);
		}
    setState({ isContainerStatusRefreshing: false });
  }

  const handleRetryError = (retryRequestData): void => {
    setForm13ModalStates({
			retryRequestData,
			isForm13UploadModalVisible: true,
			isResubmitRequest: true,
		});
  }

  const renderAction = (item) => {
    if (!actionsEnabled) {
      return;
    }

    switch (item?.status) {
      case RequestStatus.REQUESTED:
      case RequestStatus.PENDING: return (
        isContainerStatusRefreshing ? <Spin /> :
        <Icon
          type="reload"
          style={{ fontSize: 18 }}
          onClick={(e) => {
            stops(e);
            refreshContainerStatus(item?.containerNumber);
          }}
        />
      )
      case RequestStatus.REJECTED: return (
        <Button
          type="link"
          className={classes.button}
          onClick={() => handleRetryError({
            ...form13RequestData,
            containerDetails: [item],
          })}
        >
          Submit A New Request
        </Button>
      )
      default: return null;
    }
  };

  const renderStatus = (item) => {
    const updatedStatus = isSingleContainer ? updatedContainerStatus?.[form13RequestData?.id] :
      updatedContainerStatus?.[item?.containerNumber];

    const { title, ...statusStyles } = getContainerStatus(
      updatedStatus || item?.status);
    
    return <div
      className={classes.status}
      style={statusStyles}
    >
      {title}
    </div>;
  };

  const renderCollapseHeader = ({
    title,
    item,
  }: {
    title: string;
    item;
    }) => {
    return <div
      className={classes.flexRow}
      style={{ justifyContent: 'space-between' }}
    >
      <div className={classes.flexRow}>
        <div className={classes.containerTitle}>{title}</div>
        {renderStatus(item)}
      </div>
      {renderAction(item)}
    </div>;
  };
  
  const renderContainerDetails = () => {
    return form13RequestData?.containerDetails?.map((item) => (
      <Collapse
        className={classes.collapse}
      >
        <Collapse.Panel
          header={renderCollapseHeader({
            title: `Container ${item.containerNumber || '-'}`,
            item,
          })}
          key={item}
          >
          {renderContainer(item)}
        </Collapse.Panel>
      </Collapse>
    ));
  };
  
  const isSingleContainer = form13RequestData?.containerDetails?.length === 1;
  const renderContainer = (item) => {
    return <div className={classes.contentBox}>
    <div className={classes.contentRow}>
      {renderField('Container Size', item.containerSize)}
      {renderField('Container Type', getLabelFromOdexField("containerTypes", item.containerType))}
      {renderField('Temperature', item.temperature)}
    </div>
    <div className={classes.contentRow}>
      {renderField('No. of Package', item.packageCount)}
      {renderField('VGM (MT)', item.weight)}
      {renderField('VGM via ODeX', item.vgmViaOdex ? 'Yes' : 'No')}
    </div>
    <div className={classes.contentRow}>
      {renderField('Agent Seal No.', item.agentSealNo)}
      {renderField('Custom Seal No.', item.customSealNo)}
      {renderField('SB No. or Invoice No', item.shipBillInvoiceNo)}
    </div>
    <div className={classes.contentRow}>
      {form13RequestData?.cargoType === 'ODC' &&
        <>
          {renderField('ODC Units', item.odcUnits)}
          {renderField('Front Dimensions', item.frontDimensions)}
        </>
      }
      {form13RequestData?.cargoType === 'HAZARDOUS' &&
        <>
          {renderField('IMO No', item.imoNo)}
          {renderField('UN No.', item.unNo)}
        </>
      }
      {renderField('DO No.', item.doNo)}
    </div>
  </div>
  }

  const renderContainers = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Container Details')}
      {renderLine()}
      {renderContainerDetails()}
    </div>;
  };

  const renderAttachmentColumns = () => {
    return <div className={classes.flexRow} style={{ marginBottom: 16 }}>
      <div className={classes.title} style={{ width: '5%' }}>S No.</div>
      <div className={classes.title} style={{ width: '40%' }}>File Name</div>
      <div className={classes.title} style={{ width: '20%' }}>Document Type</div>
      <div className={classes.title} style={{ width: '20%' }}>Uploaded at</div>
    </div>;
  };

  const renderAttachmentValues = () => {
    const { documents } = form13RequestData || {};
    return documents?.map((item, idx) => <div
      className={classes.flexRow}
      style={{
        marginBottom: 10,
        backgroundColor: '#FCFAF4',
        padding: 8,
      }}>
      <div className={classes.value} style={{ width: '5%' }}>{idx + 1}</div>
      <Tooltip title={item.name}>
        <div className={classes.addDocument} style={{ width: '40%', backgroundColor: 'inherit' }}>
          <a href={item.url} download>
            {item.name}
          </a>
        </div>
      </Tooltip>
      <div className={classes.value} style={{ width: '20%' }}>{getLabelFromOdexField('attachmentList', item.type) || '-'}</div>
      <div className={classes.value} style={{ width: '20%' }}>
        {moment(item.createdAt).format('DD MMM YYYY | hh:mm A')}
      </div>
        <a href={item.url} download>
          <Icon type="download" style={{ fontSize: 16 }}/>
        </a>
    </div>);
  };

  const renderAttachment = () => {
    return <div className={classes.contentBox}>
      {renderContentTitle('Attachment')}
      {renderLine()}
      {renderAttachmentColumns()}
      {renderAttachmentValues()}
    </div>;
  };

  const handleDelete = async () : Promise<void> => {
    setState({ isForm13RequestDeleting: true });
    const response = await deleteForm13Request(form13RequestData?.id);
    if (response.isSuccess) {
      message.success('Form 13 Request Deleted Successfully');
      fetchAllSEDocuments(shipmentDetails?.id);
      setState({ isForm13RequestDeleting: false, isDeleteForm13RequestModalVisible: false });
    } else {
      message.error(response.errorMessage);
      setState({ isForm13RequestDeleting: false });
    }
  }

  const renderDeleteModal = () : React.ReactElement => {
    return (
      <SEDeleteOdexDraftModal
        isVisible={isDeleteForm13RequestModalVisible}
        onCancel={() => setState({ isDeleteForm13RequestModalVisible: false })}
        handleDelete={handleDelete}
        isRequestDeleting={isForm13RequestDeleting}
      />
    );
  };

  if (isFetchingShippingData) {
    return <Loader
      style={{
        marginLeft: '18%',
        marginTop: '6%',
      }}
      zIndex={1}
    />;
  }

  return <div className={classes.main}>
    {renderForm13List()}
    {!!form13Requests?.length && (
      <>
        {renderReference()}
        {renderShipperBooking()}
        {renderContractPartyDetails()}
        {renderCustomerSection()}
        {isValidArray(form13RequestData?.containerDetails) && renderContainers()}
        {isValidArray(form13RequestData?.documents) && renderAttachment()}
        {isDeleteForm13RequestModalVisible && renderDeleteModal()}
      </>
    )}
  </div>;
};

const mapStateToProps = ({ shipmentExecutionReducer, masterData }) => ({
  odexMasterData: masterData.odexMasterData,
  odexForm13PyrCode: masterData.odexForm13PyrCode,
  form13Requests: shipmentExecutionReducer.form13Requests,
  form13RequestData: shipmentExecutionReducer.form13RequestData,
  odexForm13ReferenceNumber: masterData.odexForm13ReferenceNumber,
  form13RequestError: shipmentExecutionReducer.form13RequestError,
  pendingForm13Containers: shipmentExecutionReducer.siPendingCount?.FORM13?.pendingContainerCount,
  isFetchingShippingData: shipmentExecutionReducer.isFetchingShippingData,
});

const mapDispatchToProps = dispatch => bindActionCreators({
  fetchAllSEDocuments,
  fetchForm13RequestDetails,
}, dispatch);

const options: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: vgmStyles,
  },
};

const Form13 = GenericHoc(options)(Form13Docs);
export default Form13;
